import _ from 'lodash';

import CommonError from 'app/common/error';

export const resourceNames = {
  activitySurvey: 'Activity Survey',
  auth: 'Connected Account',
  authUsage: 'Connected Account Usage',
  authTest: 'Connected Account Test',
  capabilities: 'Capabilities',
  devAppEvents: 'App Events',
  devAppEventStats: 'App Event Stats',
  devAppIssueActionTag: 'App Issue Tag',
  devAppIssues: 'App Issues',
  devAppIssueTag: 'App Issue Tag',
  devAppLokiTags: 'Issue Tags',
  dropdown: 'Dropdown Choices',
  fieldErrors: 'Field Validation',
  fileUpload: 'File',
  folder: 'Folder',
  folders: 'Folders',
  givesAll: 'Insert Fields',
  heldReasons: 'Zap Held Reasons',
  needs: 'Fields',
  node: 'Node',
  nodes: 'Nodes',
  nodeSample: 'Sample',
  nodeSubscription: 'Webhook Subscription',
  search: 'Search Results',
  sharedZap: 'Template',
  supportRequest: 'Support Request',
  service: 'service',
  services: 'services',
  taskReplay: 'Task Replay',
  taskStates: 'Task States',
  task: 'Task',
  tasks: 'Tasks',
  zap: 'Zap',
  zapAction: 'Zap Action',
  zapDelete: 'Zap Delete',
  zapNotes: 'Zap Notes',
  zapRun: 'Zap Run',
  zapRunStatus: 'Zap Run Status',
  zaps: 'Zaps',
  zapTemplates: 'Zap templates',
  zapTrigger: 'Zap Trigger',
};

const genericMessages = {
  504: 'Timed out.',
};

const helpLinks = {
  [resourceNames.dropdown]:
    '/help/troubleshoot/behavior/dropdown-menu-in-zap-field-is-empty-or-missing-options',
};

const tryJSON = response => {
  try {
    return JSON.parse(response.responseText);
  } catch (e) {
    return undefined;
  }
};

// This is purposefully kind of loose, because we don't want error handling
// code to throw errors.
export const toErrors = errors => {
  if (!errors) {
    console.warn("Don't try to create errors from a falsy value.");
    return [];
  }
  // Passed in a response.
  if (errors.responseText) {
    errors = errors.responseText;
  }
  // Passed in response text.
  if (_.isString(errors)) {
    try {
      const envelope = JSON.parse(errors);
      errors = envelope.errors;
    } catch (e) {
      console.warn('Unable to parse errors: ', errors);
      return [];
    }
  }
  if (!errors) {
    console.warn("Don't try to create errors from a falsy value.");
    return [];
  }
  // Passed in a parsed envelope.
  if (errors.errors) {
    errors = errors.errors;
  }
  if (!_.isArray(errors)) {
    console.warn('Not an errors array: ', errors);
    return [];
  }
  return errors;
};

export const hasErrorsOfType = (type, errors) => {
  errors = toErrors(errors);
  return errors.some(error => error.type === type);
};

export const selectErrorsOfType = (type, errors) => {
  errors = toErrors(errors);
  return errors.filter(error => error.type === type);
};

export const selectErrorOfType = (type, errors) =>
  selectErrorsOfType(type, errors)[0];

const getErrorDataFromResponse = response => ({
  status: response.status,
  statusText: response.statusText,
  responseText: response.responseText,
  json: tryJSON(response),
  isNetworkError:
    !!response.networkErrorMessage || !!response.fetchErrorMessage,
});

// TODO(vitorbal): most of the behavior used for `createDataError` should actually
// be extracted out and re-used by all error types that are consuming a fetch error response.
//
// Right now we have a bit of a mess where sometimes one error "type" actually contains another
// nested error type and this is very confusing.
const createDataError = verb => ({
  assign({ response, resource }) {
    return {
      ...getErrorDataFromResponse(response),
      resource,
      rootErrorMessage:
        response.networkErrorMessage || response.fetchErrorMessage,
    };
  },
  summary(error) {
    const { resource } = error;
    const resourceName = _.isString(resource) ? resource : 'item';
    if (error.status === 404) {
      return `${resourceName} not found.`;
    }
    const rootErrorMessage =
      error.rootErrorMessage || genericMessages[error.status] || '';
    const rootErrorSuffix = rootErrorMessage ? `: ${rootErrorMessage}` : '.';
    return `Problem ${verb} ${resourceName}${rootErrorSuffix}`;
  },
  helpLink(error) {
    const { resource } = error;
    return helpLinks[resource];
  },
});

const createActionError = message => ({
  assign({ action }) {
    return {
      action,
    };
  },
  summary(error) {
    return `${message}: ${JSON.stringify(error.action)}`;
  },
});

const createCustomError = (spec = {}) => ({
  assign(detail) {
    if (detail instanceof Error) {
      if (!detail.summary) {
        return {
          summary: detail.message,
        };
      }
    }

    if (_.isObject(detail)) {
      return {
        // If there's no summary and an error was passed, make the error message the summary
        ...(detail instanceof Error && !detail.summary
          ? { summary: detail.message }
          : {}),
        // If an error response was passed in, extract some error info from it
        ...(detail.response ? getErrorDataFromResponse(detail.response) : {}),
        ...detail,
      };
    }

    return {};
  },
  ...spec,
});

const Errors = CommonError.createErrorTypes({
  DataGetError: createDataError('retrieving'),

  DataPostError: createDataError('creating'),

  DataPatchError: createDataError('updating'),

  DataPutError: createDataError('updating'),

  DataDeleteError: createDataError('removing'),

  UnpauseError: {
    assign({ flowId, response }) {
      return {
        flowId,
        errors: response.errors,
        json: tryJSON(response),
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
      };
    },
    summary() {
      return 'There was a problem activating your Zap.';
    },
  },

  PauseError: {
    assign({ flowId, response }) {
      return {
        flowId,
        errors: response.errors,
        json: tryJSON(response),
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
      };
    },
    summary() {
      return 'There was a problem deactivating your Zap.';
    },
  },

  ActionNotObjectError: createActionError('Action was not an object'),

  ActionTypeNotValid: createActionError('Action type is not valid'),

  SharedZapParamError: {
    assign(detail) {
      return {
        sharedZap: detail.sharedZap || {},
        stepIndex: detail.stepIndex || -1,
        param: detail.param || {},
      };
    },

    summary(error) {
      return `Problem with param {{${error.param.id}__${error.param.key}}} of step ${error.stepIndex} of Shared Zap ${error.sharedZap.id}.`;
    },
  },

  UnknownError: createCustomError(),
  // We need this as an "alias", since the error name is visible
  PlatformError: createCustomError(),

  SignUpValidationError: {
    assign(details) {
      const fieldErrorsError = selectErrorOfType(
        'field_errors',
        details.errors
      );
      const fields = _.get(fieldErrorsError, 'fields', []);
      const fieldErrors = _.fromPairs(
        fields.map(field => [field.key, field.message])
      );
      return {
        ...details,
        fieldErrors,
      };
    },
    summary(error) {
      return (
        'Sign up problems with ' + Object.keys(error.fieldErrors).join(', ')
      );
    },
  },

  SignUpError: {
    assign(details) {
      return details;
    },
    summary() {
      return 'There was a problem while trying to sign up.';
    },
  },

  LoadItemsError: {
    assign({ resource, itemName }) {
      return { resource, itemName };
    },
    summary(error) {
      const { itemName } = error;
      return `We’re having trouble loading ‘${itemName}’ data...`;
    },
    helpLink(error) {
      const { resource } = error;
      return helpLinks[resource];
    },
  },

  LoadItemsNetworkError: {
    assign({ resource, itemName, response = {} }) {
      return {
        resource,
        itemName,
        status: response.status,
        statusText: response.statusText,
        responseText: response.responseText,
      };
    },
    summary(error) {
      const { itemName } = error;
      return `We’re having trouble loading ‘${itemName}’ data...`;
    },
    helpLink(error) {
      const { resource } = error;
      return helpLinks[resource];
    },
  },
});

export const isPermanentError = (error = {}) => {
  const type = Errors[error.name];
  return Boolean(type && type.isPermanent(error));
};

export default Errors;
